import cn from 'classnames';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { match } from 'ts-pattern';
import { useScreenContext } from '../../../../context/ScreenContext/hook';
import { BaseModalProps, ModalType } from '../types';

export const BaseModal: React.FC<React.PropsWithChildren<BaseModalProps>> = ({
  adjustForKeyboard = false,
  children,
  isShowing,
  modalType = ModalType.STANDARD,
  onBackdropClick,
  ariaLabel,
  dialogRef,
  onDialogKeyDown,
}) => {
  const { isSmallTailwindScreen, isLargeTailwindScreen } = useScreenContext();
  const [keyboardOpen, setKeyboardOpen] = useState<boolean>(false);

  useEffect(() => {
    function handleKeyboardListener() {
      const vh = window.innerHeight;
      const visualVh = window.visualViewport?.height ?? 0;

      setKeyboardOpen(visualVh < vh);
    }

    window.visualViewport?.addEventListener('resize', handleKeyboardListener);

    return () => {
      window.visualViewport?.removeEventListener('resize', handleKeyboardListener);
    };
  }, []);

  /**
   * Full-screen on mobile, centered card on tablet; `desktopClassName` sets the tablet/desktop card
   * width and height.
   */
  const fullScreenMobileConfig = (desktopClassName: string) => ({
    style: `
      max-md:inset-y-0 max-md:w-screen max-md:fixed max-md:bottom-0 max-md:left-0
      ${desktopClassName}
    `,
    show: 'max-md:animate-slide-in-up md:animate-fade-in',
    hide: 'max-md:animate-slide-out-down md:animate-fade-out',
    docked: isSmallTailwindScreen,
  });

  const { docked, hide, show, style } = match(modalType)
    .with(ModalType.FULL_SCREEN_MOBILE, () =>
      fullScreenMobileConfig('md:w-[448px] md:h-[calc(100dvh-64px)] md:rounded-xl')
    )
    .with(ModalType.FULL_SCREEN_MOBILE_STANDARD_DESKTOP, () =>
      fullScreenMobileConfig('md:w-[640px] md:max-h-[calc(100dvh-64px)] md:rounded-xl flex flex-col')
    )
    .with(ModalType.FULL_SCREEN_MOBILE_AND_TABLET, () => ({
      style: `
        max-lg:inset-y-0 max-lg:w-screen max-lg:fixed max-lg:bottom-0 max-lg:left-0
        lg:w-[448px] lg:h-[calc(100dvh-64px)] lg:rounded-xl
      `,
      show: 'max-lg:animate-slide-in-up lg:animate-fade-in',
      hide: 'max-lg:animate-slide-out-down lg:animate-fade-out',
      docked: !isLargeTailwindScreen,
    }))
    .with(ModalType.FULL_SCREEN, () => ({
      style: 'fixed inset-0 h-dvh w-screen',
      show: '',
      hide: 'animate-fade-out',
      docked: true,
    }))
    .with(ModalType.PEEK_A_BOO, () => ({
      style: 'max-w-[448px] w-full rounded-xl overflow-hidden mx-4',
      show: 'animate-fade-in',
      hide: 'animate-fade-out',
      docked: false,
    }))
    .with(ModalType.DOCKED, () => ({
      style: `
        max-lg:w-[100svw] max-lg:inset-0 max-lg:h-fit max-lg:md:h-[100svh] max-lg:fixed max-lg:top-0 max-lg:left-0 
        lg:w-[400px] lg:max-h-[95vh] lg:fixed lg:top-1 lg:left-1 lg:rounded-xl
      `,
      show: 'animate-slide-in-left',
      hide: 'animate-slide-out-left',
      docked: true,
    }))
    .otherwise(() => ({
      // ModalType.Standard styling
      style: `
        max-lg:min-h-[156px] max-lg:w-screen max-lg:fixed max-lg:rounded-t-xl max-lg:bottom-0 max-lg:left-0
        lg:w-[640px] lg:rounded-xl
      `,
      show: 'max-lg:animate-slide-in-up lg:animate-fade-in',
      hide: 'max-lg:animate-slide-out-down lg:animate-fade-out',
      docked: !isLargeTailwindScreen,
    }));

  const [animationClass, setAnimationClass] = useState<string>(show);

  useLayoutEffect(() => {
    if (isShowing) {
      setAnimationClass(show);
    }

    if (!isShowing) {
      setAnimationClass(hide);
    }
  }, [isShowing, hide, show, setAnimationClass]);
  const ownRef = useRef<HTMLDivElement>(null);
  const transitionRef = dialogRef ?? ownRef;
  const modalContent = (
    <div
      ref={transitionRef}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={onDialogKeyDown}
      id='modal'
      role='dialog'
      aria-modal='true'
      aria-label={ariaLabel}
      tabIndex={-1}
      className={cn(
        'outline-none',
        'z-modal overflow-hidden border-none bg-white drop-shadow-2xl transition',
        // FULL_SCREEN's bare `fixed` ties with this `relative` and loses on source order.
        { relative: modalType !== ModalType.FULL_SCREEN },
        style,
        animationClass,
        {
          'max-md:pb-12': keyboardOpen && adjustForKeyboard && modalType === ModalType.STANDARD,
        }
      )}>
      {children}
    </div>
  );

  const handleCloseClick = () => {
    onBackdropClick?.();
  };

  const modal = docked ? (
    modalContent
  ) : (
    <div
      className={cn(
        'fixed inset-0 left-0 top-0 z-modal-backdrop flex items-center justify-center overflow-hidden overscroll-contain pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)]',
        {}
      )}
      onClick={handleCloseClick}
      onWheel={(e) => {
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onTouchMove={(e) => {
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}>
      {modalContent}
    </div>
  );

  return (
    <CSSTransition nodeRef={transitionRef} in={isShowing} unmountOnExit timeout={150}>
      {modal}
    </CSSTransition>
  );
};
